import type { CSSProperties, FC, PointerEvent, ReactNode } from 'react'
import type { ChartAnimation } from '@/components/charts/chart-utils'

import { Fragment, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'

import {
    areaPath,
    bandPath,
    domainOf,
    linePath,
    toPoints,
    useChartId
} from '@/components/charts/chart-utils'

export interface AreaSeries {
    name: string
    values: Array<number>
    color: string
    fill?: boolean
    fadeFrom?: number
}

interface AreaChartProps {
    title: string
    series: Array<AreaSeries>
    labels: Array<string>
    band?: {
        low: Array<number>
        high: Array<number>
        color: string
        from: number
    }
    height?: number
    domain?: [number, number]
    gridLines?: number
    showGrid?: boolean
    showLabels?: boolean
    labelEvery?: number
    formatValue?: (value: number) => string
    formatAxis?: (value: number) => string
    interactive?: boolean
    animation?: ChartAnimation
    delay?: number
    marker?: {
        index: number
        label: string
    }
    surface?: 'light' | 'dark'
    className?: string
}

const defaultFormat = (value: number): string => value.toLocaleString('en-US')

export const AreaChart: FC<AreaChartProps> = ({
    title,
    series,
    labels,
    band,
    height = 160,
    domain,
    gridLines = 3,
    showGrid = true,
    showLabels = true,
    labelEvery = 1,
    formatValue = defaultFormat,
    formatAxis,
    interactive = true,
    animation = 'inview',
    delay = 0,
    marker,
    surface = 'light',
    className
}): ReactNode => {
    const id = useChartId('area')
    const [active, setActive] = useState<number | null>(null)
    const count = labels.length

    const resolvedDomain = useMemo<[number, number]>(() => {
        if (domain) return domain

        const groups = series.map(item => item.values)

        if (band) groups.push(band.low, band.high)

        return domainOf(groups)
    }, [domain, series, band])

    const shapes = useMemo(() => {
        return series.map(item => {
            const points = toPoints(item.values, resolvedDomain, count)
            const solid = item.fadeFrom !== undefined ? points.slice(0, item.fadeFrom + 1) : points
            const faded = item.fadeFrom !== undefined ? points.slice(item.fadeFrom) : []

            return {
                points,
                area: item.fill ? areaPath(points) : '',
                solid: linePath(solid),
                faded: faded.length > 1 ? linePath(faded) : ''
            }
        })
    }, [series, resolvedDomain, count])

    const bandShape = useMemo(() => {
        if (!band) return ''

        const high = toPoints(band.high, resolvedDomain, count).slice(band.from)
        const low = toPoints(band.low, resolvedDomain, count).slice(band.from)

        return bandPath(high, low)
    }, [band, resolvedDomain, count])

    const ticks = useMemo(() => {
        if (!showGrid) return []

        const [min, max] = resolvedDomain

        return Array.from({ length: gridLines + 1 }, (_, index) => {
            const ratio = index / gridLines

            return { top: ratio * 100, value: max - (max - min) * ratio }
        })
    }, [showGrid, gridLines, resolvedDomain])

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const rect = event.currentTarget.getBoundingClientRect()
        const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))

        setActive(Math.round(ratio * (count - 1)))
    }

    const activeX = active !== null && count > 1 ? (active / (count - 1)) * 100 : 0
    const tooltipX = Math.min(82, Math.max(18, activeX))
    const isDark = surface === 'dark'

    return (
        <figure
            className={cn('relative', animation === 'autoplay' && 'chart-autoplay', className)}
            data-chart={animation === 'manual' ? undefined : ''}
            data-inview={animation === 'autoplay' ? 'true' : undefined}
            style={{ '--chart-delay': `${delay}ms` } as CSSProperties}
        >
            <div className='relative' style={{ height }}>
                {ticks.map(tick => (
                    <div
                        key={tick.top}
                        aria-hidden='true'
                        className={cn('absolute inset-x-0 border-t border-dashed', isDark ? 'border-cream/10' : 'border-burgundy/[0.09]')}
                        style={{ top: `${tick.top}%` }}
                    >
                        {formatAxis && (
                            <span className={cn('absolute -top-[15px] left-0 text-[10px] tabular-nums', isDark ? 'text-cream/45' : 'text-stone')}>
                                {formatAxis(tick.value)}
                            </span>
                        )}
                    </div>
                ))}

                <div className='chart-wipe absolute inset-0' data-chart-wipe=''>
                    <svg
                        aria-hidden='true'
                        focusable='false'
                        viewBox='0 0 100 100'
                        preserveAspectRatio='none'
                        className='absolute inset-0 size-full overflow-visible'
                    >
                        <defs>
                            {series.map((item, index) => (
                                <linearGradient key={item.name} id={`${id}-fill-${index}`} x1='0' y1='0' x2='0' y2='1'>
                                    <stop offset='0%' stopColor={item.color} stopOpacity={isDark ? 0.32 : 0.2} />
                                    <stop offset='100%' stopColor={item.color} stopOpacity={0} />
                                </linearGradient>
                            ))}
                        </defs>

                        {band && bandShape && (
                            <path d={bandShape} fill={band.color} fillOpacity={0.16} />
                        )}

                        {shapes.map((shape, index) => (
                            <Fragment key={series[index].name}>
                                {shape.area && <path d={shape.area} fill={`url(#${id}-fill-${index})`} />}

                                <path
                                    d={shape.solid}
                                    fill='none'
                                    stroke={series[index].color}
                                    strokeWidth={2}
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    vectorEffect='non-scaling-stroke'
                                />

                                {shape.faded && (
                                    <path
                                        d={shape.faded}
                                        fill='none'
                                        stroke={series[index].color}
                                        strokeOpacity={0.45}
                                        strokeWidth={2}
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        vectorEffect='non-scaling-stroke'
                                    />
                                )}
                            </Fragment>
                        ))}
                    </svg>
                </div>

                {marker && count > 1 && (
                    <div
                        aria-hidden='true'
                        className={cn('absolute bottom-0 top-0 border-l border-dashed', isDark ? 'border-cream/25' : 'border-burgundy/25')}
                        style={{ left: `${(marker.index / (count - 1)) * 100}%` }}
                    >
                        <span
                            className={cn(
                                'absolute -top-1 left-1.5 whitespace-nowrap rounded-full px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em]',
                                isDark ? 'bg-cream/10 text-cream/70' : 'bg-burgundy/[0.06] text-stone-dark'
                            )}
                        >
                            {marker.label}
                        </span>
                    </div>
                )}

                {interactive && (
                    <div
                        className='absolute inset-0 cursor-crosshair touch-pan-y'
                        onPointerMove={onPointerMove}
                        onPointerDown={onPointerMove}
                        onPointerLeave={() => setActive(null)}
                    >
                        {active !== null && (
                            <>
                                <div
                                    aria-hidden='true'
                                    className={cn('absolute bottom-0 top-0 w-px', isDark ? 'bg-cream/30' : 'bg-burgundy/25')}
                                    style={{ left: `${activeX}%` }}
                                />

                                {shapes.map((shape, index) => (
                                    <span
                                        key={series[index].name}
                                        aria-hidden='true'
                                        className={cn('absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2', isDark ? 'border-burgundy' : 'border-ivory')}
                                        style={{
                                            left: `${shape.points[active][0]}%`,
                                            top: `${shape.points[active][1]}%`,
                                            backgroundColor: series[index].color
                                        }}
                                    />
                                ))}

                                <div
                                    role='status'
                                    className='pointer-events-none absolute top-1 z-10 min-w-[120px] -translate-x-1/2 rounded-xl border border-burgundy/10 bg-ivory/95 px-3 py-2 text-left shadow-[0_12px_32px_-16px_rgb(57_26_24/0.45)] backdrop-blur'
                                    style={{ left: `${tooltipX}%` }}
                                >
                                    <p className='font-mono text-[10px] uppercase tracking-[0.14em] text-stone'>
                                        {labels[active]}
                                    </p>

                                    {series.map(item => (
                                        <p key={item.name} className='mt-1 flex items-center gap-2 text-xs text-burgundy'>
                                            <span aria-hidden='true' className='size-2 rounded-full' style={{ backgroundColor: item.color }} />
                                            <span className='text-stone-dark'>{item.name}</span>
                                            <span className='ml-auto font-medium tabular-nums'>{formatValue(item.values[active])}</span>
                                        </p>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {showLabels && (
                <div aria-hidden='true' className='relative mt-2 h-4'>
                    {labels.map((label, index) => (
                        index % labelEvery === 0 || index === count - 1 ? (
                            <span
                                key={`${label}-${index}`}
                                className={cn(
                                    'absolute top-0 text-[10px] tabular-nums',
                                    index === 0 ? 'translate-x-0' : index === count - 1 ? '-translate-x-full' : '-translate-x-1/2',
                                    isDark ? 'text-cream/45' : 'text-stone'
                                )}
                                style={{ left: `${count > 1 ? (index / (count - 1)) * 100 : 50}%` }}
                            >
                                {label}
                            </span>
                        ) : null
                    ))}
                </div>
            )}

            <figcaption className='sr-only'>{title}</figcaption>

            <table className='sr-only'>
                <thead>
                    <tr>
                        <th scope='col'>Period</th>
                        {series.map(item => <th key={item.name} scope='col'>{item.name}</th>)}
                    </tr>
                </thead>

                <tbody>
                    {labels.map((label, index) => (
                        <tr key={`${label}-${index}`}>
                            <th scope='row'>{label}</th>
                            {series.map(item => <td key={item.name}>{formatValue(item.values[index])}</td>)}
                        </tr>
                    ))}
                </tbody>
            </table>
        </figure>
    )
}
